import { useState } from 'react';
import { Skeleton } from '@mui/material';
import { useGetAdminTransactionsQuery } from 'store/services/admin/adminApi';
import { AdminPointTransaction } from 'types/interfaces/transaction';
import AdminLayout from '../AdminLayout';
import Points from 'components/Points';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { getAvatarPlaceholder } from 'utils/avatarUtils';
import './AdminTransactionsPage.css';

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'TRADE_CREDIT', label: 'Trade' },
  { value: 'TRADE_FEE', label: 'Trade Fee' },
  { value: 'MONTHLY_FEE', label: 'Monthly Fee' },
  { value: 'REFUND', label: 'Refund' },
  { value: 'ADMIN_ADJUSTMENT', label: 'Admin Adjustment' },
  { value: 'SIGNUP_BONUS', label: 'Signup Bonus' },
  { value: 'OTHER', label: 'Other' }
];

const AdminTransactionsPage = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const pageSize = 50;

  const { data, isLoading, error, refetch } = useGetAdminTransactionsQuery({
    page,
    pageSize,
    search: searchTerm,
    typeFilter,
  });

  // Format date as MM/DD/YY HH:MM (UTC)
  const formatTransactionDate = (isoDateString: string): string => {
    const date = new Date(isoDateString);

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = String(date.getUTCFullYear()).slice(-2);
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours}:${minutes}`;
  };

  // Get error message based on error status
  const getErrorMessage = (error: any): string => {
    const status = error?.status || error?.originalStatus;

    if (status === 401) return "Please log in to view transactions";
    if (status === 403) return "You do not have permission to view this resource";
    if (status === 404) return "Transaction data is temporarily unavailable";
    if (status === 500) return "Server error. Our team has been notified. Please try again in a few minutes.";
    if (status === 'FETCH_ERROR') return "Network error. Please check your internet connection.";
    if (status === 'TIMEOUT_ERROR') return "Request timed out. Please try again.";

    return "Unable to load transactions. Please try again.";
  };

  // Determine if retry button should be shown
  const shouldShowRetry = (error: any): boolean => {
    const status = error?.status || error?.originalStatus;
    return status !== 401 && status !== 403;
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (data && page < data.pagination.totalPages) {
      setPage(page + 1);
    }
  };

  // Handle type filter selection
  const handleTypeFilterChange = (value: string) => {
    setTypeFilter(value);
    setIsTypeDropdownOpen(false);
    setPage(1); // Reset to first page when filter changes
  };

  // Get selected type label
  const getSelectedTypeLabel = () => {
    const selected = TYPE_OPTIONS.find(opt => opt.value === typeFilter);
    return selected ? selected.label : 'All Types';
  };

  // Loading state
  if (isLoading) {
    return (
      <AdminLayout title="Admin Panel - Transactions" currentPath="/admin/transactions">
        <div className="admin-transactions-page">
          <div className="admin-transactions-header">
            <h2 className="admin-transactions-title">Global Transaction Ledger</h2>
            <p className="admin-transactions-subtitle">
              View all point transactions across all users
            </p>
          </div>

          <div className="admin-transactions-controls">
            <input
              type="text"
              placeholder="Search by username or trade number"
              className="admin-transactions-search-input"
              disabled
            />
            <div className="admin-transactions-filter-dropdown">
              <span>All Types</span>
              <img
                src={AssetUrls.icons.dropdowns.dropdownArrow}
                alt="dropdown"
                className="dropdown-arrow"
              />
            </div>
          </div>

          <div className="admin-transactions-content">
            <div className="admin-transactions-table">
              <div className="admin-transactions-table-header">
                <div className="admin-transactions-col-username">Username</div>
                <div className="admin-transactions-col-date">Date</div>
                <div className="admin-transactions-col-type">Type</div>
                <div className="admin-transactions-col-amount">Amount</div>
                <div className="admin-transactions-col-description">Description</div>
              </div>
              <div className="admin-transactions-loading">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="admin-transaction-row-skeleton">
                    <Skeleton variant="text" width={150} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={140} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width={120} height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                    <Skeleton variant="text" width="100%" height={20} sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <AdminLayout title="Admin Panel - Transactions" currentPath="/admin/transactions">
        <div className="admin-transactions-page">
          <div className="admin-transactions-header">
            <h2 className="admin-transactions-title">Global Transaction Ledger</h2>
          </div>

          <div className="admin-transactions-content">
            <div className="admin-transactions-error-state">
              <div className="error-icon">⚠️</div>
              <h2 className="error-title">Unable to Load Transactions</h2>
              <p className="error-message">{getErrorMessage(error)}</p>
              {shouldShowRetry(error) && (
                <MagicButton
                  label="Try Again"
                  color="purple"
                  onPress={() => refetch()}
                />
              )}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Empty state
  if (data && data.transactions.length === 0) {
    return (
      <AdminLayout title="Admin Panel - Transactions" currentPath="/admin/transactions">
        <div className="admin-transactions-page">
          <div className="admin-transactions-header">
            <h2 className="admin-transactions-title">Global Transaction Ledger</h2>
            <p className="admin-transactions-subtitle">
              View all point transactions across all users
            </p>
          </div>

          <div className="admin-transactions-controls">
            <input
              type="text"
              placeholder="Search by username or trade number"
              className="admin-transactions-search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page on search
              }}
            />
            <div
              className="admin-transactions-filter-dropdown"
              onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
            >
              <span>{getSelectedTypeLabel()}</span>
              <img
                src={AssetUrls.icons.dropdowns.dropdownArrow}
                alt="dropdown"
                className="dropdown-arrow"
              />
              {isTypeDropdownOpen && (
                <div className="filter-dropdown-menu">
                  {TYPE_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      className={`filter-dropdown-item ${typeFilter === option.value ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTypeFilterChange(option.value);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="admin-transactions-content">
            <div className="admin-transactions-empty-state">
              <div className="empty-state-icon">
                <img
                  src={`${AssetUrls.base}/assets/icons/receipt.svg`}
                  alt="No transactions"
                  style={{ width: 64, height: 64, opacity: 0.3 }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <h2 className="empty-state-title">No Transactions Found</h2>
              <p className="empty-state-description">
                {searchTerm || typeFilter !== 'all'
                  ? "No transactions match your search criteria. Try adjusting your filters."
                  : "There are no transactions in the system yet."}
              </p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Success state with data
  return (
    <AdminLayout title="Admin Panel - Transactions" currentPath="/admin/transactions">
      <div className="admin-transactions-page">
        <div className="admin-transactions-header">
          <h2 className="admin-transactions-title">Global Transaction Ledger</h2>
          <p className="admin-transactions-subtitle">
            View all point transactions across all users
          </p>
        </div>

        <div className="admin-transactions-controls">
          <input
            type="text"
            placeholder="Search by username or trade number"
            className="admin-transactions-search-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1); // Reset to first page on search
            }}
          />
          <div
            className="admin-transactions-filter-dropdown"
            onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
          >
            <span>{getSelectedTypeLabel()}</span>
            <img
              src={AssetUrls.icons.dropdowns.dropdownArrow}
              alt="dropdown"
              className="dropdown-arrow"
            />
            {isTypeDropdownOpen && (
              <div className="filter-dropdown-menu">
                {TYPE_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    className={`filter-dropdown-item ${typeFilter === option.value ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeFilterChange(option.value);
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="admin-transactions-content">
          <div className="admin-transactions-table">
            {/* Table Header */}
            <div className="admin-transactions-table-header">
              <div className="admin-transactions-col-username">Username</div>
              <div className="admin-transactions-col-date">Date</div>
              <div className="admin-transactions-col-type">Type</div>
              <div className="admin-transactions-col-amount">Amount</div>
              <div className="admin-transactions-col-description">Description</div>
            </div>

            {/* Table Body */}
            <div className="admin-transactions-table-body">
              {data?.transactions.map((transaction: AdminPointTransaction) => {
                const avatarPlaceholder = getAvatarPlaceholder(transaction.username);
                const hasValidAvatar = transaction.avatarUrl &&
                                      transaction.avatarUrl !== '' &&
                                      transaction.avatarUrl !== 'null' &&
                                      transaction.avatarUrl !== 'undefined';

                return (
                  <div key={transaction.id} className="admin-transaction-row">
                    <div className="admin-transactions-col-username">
                      <div className="username-cell">
                        {hasValidAvatar && transaction.avatarUrl ? (
                          <img
                            src={transaction.avatarUrl}
                            alt={transaction.username}
                            className="user-avatar"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const placeholder = target.nextElementSibling as HTMLElement;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        {avatarPlaceholder ? (
                          <div
                            className="user-avatar user-avatar-placeholder"
                            style={{
                              backgroundColor: avatarPlaceholder.color,
                              display: hasValidAvatar ? 'none' : 'flex'
                            }}
                          >
                            <span className="user-avatar-letter">
                              {avatarPlaceholder.letter}
                            </span>
                          </div>
                        ) : (
                          <div
                            className="user-avatar user-avatar-placeholder"
                            style={{
                              backgroundColor: '#8409D3',
                              display: hasValidAvatar ? 'none' : 'flex'
                            }}
                          >
                            <span className="user-avatar-letter">?</span>
                          </div>
                        )}
                        <span className="username-text">{transaction.username}</span>
                      </div>
                    </div>
                    <div className="admin-transactions-col-date">
                      {formatTransactionDate(transaction.createdAt)}
                    </div>
                    <div className="admin-transactions-col-type">
                      {transaction.typeDisplay}
                    </div>
                    <div className="admin-transactions-col-amount">
                      <div className={`transaction-amount ${transaction.amount >= 0 ? 'positive' : 'negative'}`}>
                        <span className="amount-sign">
                          {transaction.amount >= 0 ? '+' : '-'}
                        </span>
                        <Points points={Math.abs(transaction.amount)} type="small" />
                      </div>
                    </div>
                    <div className="admin-transactions-col-description">
                      {transaction.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Pagination Controls */}
          {data && data.pagination.totalPages > 1 && (
            <div className="pagination-controls">
              <MagicButton
                label="Previous"
                color="grey"
                onPress={handlePreviousPage}
                isDisabled={page === 1}
              />

              <span className="pagination-info">
                Page {data.pagination.currentPage} of {data.pagination.totalPages}
                {' '}({data.pagination.totalTransactions} total transactions)
              </span>

              <MagicButton
                label="Next"
                color="grey"
                onPress={handleNextPage}
                isDisabled={page >= data.pagination.totalPages}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTransactionsPage;
